import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import type { OrderArbeitsplatz, PurchaseDocument, PurchaseOrderFile, PurchaseShipment } from "@/api/purchase";
import { useCreatePurchaseShipment } from "@/api/purchase";
import { Button } from "@/components/ui/button";
import { fehlerText } from "@/lib/fehlerText";
import { AufgabeKarte, type ArbeitsplatzAktionen, type ArbeitsplatzRechte, type FormularZiel } from "./AufgabeKarte";
import { PositionenPanel } from "./Positionen";
import { SeitenPanels } from "./SeitenPanels";
import { SpurKarte } from "./SpurKarte";
import {
  BESTELL_FARBE, BESTELL_TEXTE, SCHRITT_TEXTE, aktuellerSchritt, containerNummern, containerTitel, schrittText, spurFarbe,
  type Spur,
} from "./schritte";

interface ArbeitsplatzProps {
  order: PurchaseOrderFile;
  arbeitsplatz: OrderArbeitsplatz;
  documents: PurchaseDocument[];
  rechte: ArbeitsplatzRechte;
  aktionen: ArbeitsplatzAktionen;
  /** Ein Upload-Lauf der Akte laeuft gerade (sperrt die Upload-Knoepfe). */
  uploadLaeuft: boolean;
}

/**
 * Arbeitsplatz (Schnitt 1): links die Spuren (je Container + Bestellung), in der Mitte die
 * eine Aufgabe, rechts die Panels. Reine Darstellung der Server-Ableitung. Rechnung, Zahlungen,
 * Kosten, Container-Daten und „Container anlegen“ werden hier erfasst (21.10.2026 „alles in der
 * Maske“); Dialoge (WE, Bestand buchen, Misch-EK) und die restlichen Spruenge laufen ueber die Akte.
 * Schnitt 3: unter der Aufgabe der Bestellspur die Positionen (Menge/EK, Hinweise, Bedarf → GA,
 * Loeschen nach Rueckfrage); die Belegliste rechts lehnt ab, ordnet zu und loescht.
 */
export function Arbeitsplatz({ order, arbeitsplatz, documents, rechte, aktionen, uploadLaeuft }: ArbeitsplatzProps) {
  // null = der Auswahl des Servers folgen ("jetzt dran"); ein Klick haelt eine Spur fest.
  // Startwert aus ?spur= (Rollen-Postfach); eine unbekannte Spur faellt unten auf die Vorgabe zurueck.
  const [searchParams] = useSearchParams();
  const [gewaehlt, setGewaehlt] = useState<string | null>(() => searchParams.get("spur"));
  // Hauptknopf → Formular in der Seitenspalte; n zaehlt, damit derselbe Knopf erneut fokussiert.
  const [anfrage, setAnfrage] = useState<{ ziel: FormularZiel; n: number } | null>(null);
  const createShipment = useCreatePurchaseShipment(order.id);

  if (arbeitsplatz.modus !== "spuren") {
    return (
      <section aria-labelledby="arbeitsplatz-titel" className="rounded-md border border-line bg-panel p-4">
        <h2 id="arbeitsplatz-titel" className="font-display text-sm font-bold uppercase tracking-wide text-ink-2">Arbeitsplatz</h2>
        {arbeitsplatz.modus === "entwurf" ? (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ink">
              Die Bestellung ist noch ein Entwurf. Positionen und Lieferant prüfen, dann den Status auf „bestellt“ setzen.
            </p>
            <Button type="button" onClick={aktionen.fokusStatus}>Bestellung abschicken</Button>
          </div>
        ) : arbeitsplatz.modus === "ohne_container" && arbeitsplatz.container_anlegen === false ? (
          <p className="mt-2 text-sm text-ink-2">Keine Aufgaben: die Bestellung ist abgeschlossen und hat keinen Container.</p>
        ) : arbeitsplatz.modus === "ohne_container" ? (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ink">Die Bestellung läuft, aber es gibt noch keinen Container. Der Arbeitsplatz beginnt mit dem ersten Container.</p>
            {rechte.canEditAkte ? (
              <Button
                type="button"
                disabled={createShipment.isPending}
                onClick={() => createShipment.mutate(
                  { label: `Container ${order.shipments.length + 1}` },
                  {
                    onSuccess: () => toast.success("Container angelegt"),
                    onError: (e) => toast.error(fehlerText(e, "Container fehlgeschlagen")),
                  },
                )}
              >
                Container anlegen
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="mt-2 text-sm text-ink-2">Keine Aufgaben: die Bestellung ist {order.archived_at ? "archiviert" : "storniert"}.</p>
        )}
      </section>
    );
  }

  const spuren: Spur[] = [...arbeitsplatz.spuren, arbeitsplatz.bestellspur];
  const jetztDran = arbeitsplatz.jetzt_dran;
  const vorgabe = jetztDran?.spur_key ?? spuren[0]!.key;
  // Verschwindet die festgehaltene Spur (Container geloescht), faellt die Auswahl zurueck.
  const aktivKey = gewaehlt !== null && spuren.some((s) => s.key === gewaehlt) ? gewaehlt : vorgabe;
  const aktiv = spuren.find((s) => s.key === aktivKey) ?? spuren[0]!;

  const shipmentVon = (spur: Spur) => (spur.typ === "container" ? order.shipments.find((s) => s.id === spur.shipment_id) ?? null : null);
  // C-Nummer nach der Bezeichnung („Container 1“ → C1), sonst nach der Reihenfolge der Spuren.
  const nummern = containerNummern(order.shipments, arbeitsplatz.spuren.map((s) => s.shipment_id));
  const titelVon = (spur: Spur) =>
    spur.typ === "container" ? containerTitel(shipmentVon(spur) ?? undefined, nummern.get(spur.shipment_id)!) : "Bestellung";
  const farbeVon = (spur: Spur) => (spur.typ === "container" ? spurFarbe(arbeitsplatz.spuren.indexOf(spur)) : BESTELL_FARBE);
  const containerTitelVon = (s: PurchaseShipment) => containerTitel(s, nummern.get(s.id)!);

  const schritt = aktuellerSchritt(aktiv, jetztDran);

  return (
    <section aria-labelledby="arbeitsplatz-titel" className="rounded-md border border-line bg-panel p-3 sm:p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="arbeitsplatz-titel" className="font-display text-sm font-bold uppercase tracking-wide text-ink-2">Arbeitsplatz</h2>
        <span className="text-xs text-ink-2">
          {jetztDran ? <>Jetzt dran: <strong className="text-ink">{titelVon(spuren.find((s) => s.key === jetztDran.spur_key) ?? aktiv)}</strong></>
            : arbeitsplatz.fertig ? <strong role="status" className="text-success">Bestellung fertig · alle Spuren erledigt, kein Rest offen</strong>
              : "Nichts dringend."}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[272px_minmax(0,1fr)_272px] xl:grid-cols-[272px_minmax(0,1fr)_300px] lg:items-start">
        <nav aria-label="Spuren" className="grid content-start gap-2">
          {spuren.map((spur) => {
            const s = aktuellerSchritt(spur, jetztDran);
            const istJetzt = jetztDran?.spur_key === spur.key;
            const label = s === null ? "Erledigt:" : s.status === "wait" ? "Wartet:" : s.status === "fix" ? "Klärung:" : istJetzt ? "Jetzt:" : "Nächster:";
            const text = s === null ? "alles abgeschlossen" : schrittText(s).aufgabe;
            const sh = shipmentVon(spur);
            const untertitel = spur.typ === "container"
              ? (sh?.eta_door ? `ETA Tür ${new Date(sh.eta_door).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", timeZone: "Europe/Berlin" })}` : sh?.label ?? "")
              : spur.saldo === "bezahlt" ? "bezahlt" : "offen";
            const felder = spur.typ === "container"
              ? spur.schritte.map((x) => ({ nr: x.nr, kurz: SCHRITT_TEXTE[x.key].kurz, status: x.status }))
              : spur.schritte.map((x) => ({ nr: x.nr, kurz: BESTELL_TEXTE[x.key].kurz, status: x.status }));
            return (
              <SpurKarte
                key={spur.key}
                spurKey={spur.key}
                titel={titelVon(spur)}
                untertitel={untertitel}
                jetztLabel={label}
                jetztText={text}
                schritte={felder}
                farbe={farbeVon(spur)}
                ausgewaehlt={spur.key === aktiv.key}
                onSelect={setGewaehlt}
              />
            );
          })}
        </nav>

        <div className="grid min-w-0 content-start gap-3">
          <AufgabeKarte
            spur={aktiv}
            schritt={schritt}
            shipment={shipmentVon(aktiv)}
            order={order}
            titel={titelVon(aktiv)}
            farbe={farbeVon(aktiv)}
            rechte={rechte}
            aktionen={{ ...aktionen, oeffneFormular: (ziel) => setAnfrage((a) => ({ ziel, n: (a?.n ?? 0) + 1 })) }}
            documents={documents}
            uploadLaeuft={uploadLaeuft}
          />
          {aktiv.typ === "bestellung" ? (
            <PositionenPanel order={order} darf={rechte.canEditAkte} mitPreis={rechte.hasPricePerm} onHinzufuegen={aktionen.oeffnePositionen} />
          ) : null}
        </div>

        <SeitenPanels
          order={order}
          shipment={shipmentVon(aktiv)}
          documents={documents}
          withPrices={order.paid_usd !== null || order.invoice_total_usd !== null}
          saldo={arbeitsplatz.bestellspur.saldo}
          darfPreise={rechte.hasPricePerm}
          darfBearbeiten={rechte.canEditAkte}
          darfKostenFreigeben={rechte.darfKostenFreigeben}
          anfrage={anfrage}
          containerTitelVon={containerTitelVon}
          zielText={titelVon(aktiv)}
          uploadLaeuft={uploadLaeuft}
          onBelegeHochladen={(dateien) => aktionen.belegeHochladen(dateien, shipmentVon(aktiv)?.id ?? null)}
          onBelegPruefen={aktionen.pruefeBeleg}
          onBelegAblehnen={aktionen.lehneBelegAb}
          onBelegKorrigieren={(documentId) => aktionen.korrektur("reopen_document", documentId)}
          onBelegLoeschen={aktionen.loescheBeleg}
        />
      </div>
    </section>
  );
}
